import React, { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { useNavigate } from "react-router-dom";
import { UserCog, Mail, Phone, KeyRound, Eye, EyeOff, Save, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();

  const [fullName, setFullName] = useState(user?.full_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [currentPwForEmail, setCurrentPwForEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  if (!user) return <Loading/>;
  const emailChanged = email.trim().toLowerCase() !== (user.email || "").toLowerCase();

  const saveProfile = async (e) => {
    e.preventDefault();
    if (emailChanged && !currentPwForEmail) {
      toast.error("Confirm your current password to change email");
      return;
    }
    setSavingProfile(true);
    try {
      const body = { full_name: fullName, phone };
      if (emailChanged) { body.email = email; body.current_password = currentPwForEmail; }
      await api.patch("/auth/me-update", body);
      toast.success("Profile updated");
      setCurrentPwForEmail("");
      if (refreshMe) await refreshMe();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: oldPw, new_password: newPw });
      toast.success("Password changed — please sign in again");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setChangingPw(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="fade-up">
        <h1 className="font-serif text-3xl font-bold text-[#1A2E24] flex items-center gap-3"><UserCog size={28} className="text-[#C9A227]"/> My Profile</h1>
        <p className="text-[#5E7166] text-sm">Update your personal info and account password.</p>
      </div>

      <Card title="Profile">
        <form onSubmit={saveProfile} className="space-y-4" data-testid="profile-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Full name</span>
              <input type="text" value={fullName} onChange={e=>setFullName(e.target.value)} className="input-modav mt-1" required data-testid="profile-fullname"/>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Phone</span>
              <div className="relative mt-1">
                <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5E7166] pointer-events-none"/>
                <input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} className="input-modav pl-11" placeholder="+234..." data-testid="profile-phone"/>
              </div>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Email</span>
            <div className="relative mt-1">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5E7166] pointer-events-none"/>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} className="input-modav pl-11" required data-testid="profile-email"/>
            </div>
            {emailChanged && (
              <div className="mt-3 p-3 bg-[#FDF6E3] border border-[#C9A227]/30 rounded-lg">
                <div className="text-xs text-[#7A6810] mb-2 flex items-center gap-1"><ShieldCheck size={14}/> Email change — confirm your current password</div>
                <input type="password" value={currentPwForEmail} onChange={e=>setCurrentPwForEmail(e.target.value)}
                       className="input-modav text-sm" placeholder="Current password" data-testid="profile-email-current-pw"/>
              </div>
            )}
          </label>
          <button type="submit" disabled={savingProfile} className="btn-primary inline-flex items-center gap-2" data-testid="profile-save">
            <Save size={16}/> {savingProfile ? "Saving..." : "Save profile"}
          </button>
        </form>
      </Card>

      <Card title="Change password">
        <form onSubmit={changePassword} className="space-y-4 max-w-md" data-testid="change-password-form">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Current password</span>
            <div className="relative mt-1">
              <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5E7166] pointer-events-none"/>
              <input type={showOld ? "text" : "password"} value={oldPw} onChange={e=>setOldPw(e.target.value)} className="input-modav pl-11 pr-11" required data-testid="profile-old-pw"/>
              <button type="button" onClick={()=>setShowOld(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E7166]" data-testid="toggle-old-pw">
                {showOld ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">New password (8+ characters)</span>
            <div className="relative mt-1">
              <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5E7166] pointer-events-none"/>
              <input type={showNew ? "text" : "password"} value={newPw} onChange={e=>setNewPw(e.target.value)} className="input-modav pl-11 pr-11" required data-testid="profile-new-pw"/>
              <button type="button" onClick={()=>setShowNew(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E7166]" data-testid="toggle-new-pw">
                {showNew ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Confirm new password</span>
            <input type={showNew ? "text" : "password"} value={confirmPw} onChange={e=>setConfirmPw(e.target.value)} className="input-modav mt-1" required data-testid="profile-confirm-pw"/>
          </label>
          <button type="submit" disabled={changingPw} className="btn-primary inline-flex items-center gap-2" data-testid="profile-change-pw-submit">
            <ShieldCheck size={16}/> {changingPw ? "Updating..." : "Change password"}
          </button>
          <p className="text-xs text-[#5E7166]">After changing, all other devices will be signed out automatically.</p>
        </form>
      </Card>
    </div>
  );
}
